/**
 * Painel Berytus › Curadoria: contestações de dados do catálogo mestre feitas pelos clientes ("Contestar dado") e
 * pedidos de inclusão de medicamento fora do mestre. Aceitar ou recusar com resposta (o cliente vê); prazo-alvo em
 * dias úteis (atrasadas em vermelho). A correção do dado em si é feita na ficha, em Catálogo mestre.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';

interface Contest { id: string; tenantId: string; tenantName: string; productId: string | null; sku: string | null; catalogProductId: string | null; gtin: string | null;
  field: string; fieldLabel: string; currentValue: string | null; suggestedValue: string | null; note: string | null; hasAttachment: boolean; status: string;
  response: string | null; createdAt: string; dueAt: string; late: boolean }
const TABS: [string, string][] = [['open', 'Abertas'], ['accepted', 'Aceitas'], ['rejected', 'Recusadas'], ['all', 'Todas']];

export function AdminContests() {
  const [status, setStatus] = useState('open');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'contests', status], queryFn: () => api<{ items: Contest[] }>(`/admin/contests?status=${status}`) });
  const decide = useMutation({
    mutationFn: (v: { c: Contest; decision: 'accept' | 'reject'; response: string }) => api(`/admin/contests/${v.c.tenantId}/${v.c.id}/decide`, { method: 'POST', json: { decision: v.decision, response: v.response } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'contests'] }),
  });
  const ask = (c: Contest, decision: 'accept' | 'reject') => {
    const r = window.prompt(decision === 'accept' ? 'Resposta ao cliente (o que foi corrigido):' : 'Motivo da recusa (o cliente vê):');
    if (r && r.trim().length >= 3) decide.mutate({ c, decision, response: r.trim() });
  };
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Curadoria: contestações</h1>
          <p>Dados do catálogo mestre contestados pelos clientes e pedidos de inclusão de medicamento. Corrija a ficha em Catálogo mestre e responda aqui.</p>
        </div>
      </div>
      <div className="seg" role="group" aria-label="Situação" style={{ marginBottom: 12 }}>
        {TABS.map(([v, l]) => <button key={v} type="button" aria-pressed={status === v} onClick={() => setStatus(v)}>{l}</button>)}
      </div>
      {decide.error && <ErrorBox error={decide.error} />}
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : !q.data.items.length ? <div className="empty"><b>Nada aqui</b>Nenhuma contestação nesta situação.</div> : (
        <section className="panel flush">
          <div className="grid-wrap">
            <table className="grid" style={{ width: '100%', minWidth: 960 }}>
              <thead><tr><th>Conta / SKU</th><th>Campo</th><th>Atual no mestre</th><th>Sugerido</th><th>Prazo</th><th>Situação</th><th /></tr></thead>
              <tbody>
                {q.data.items.map((c) => (
                  <tr key={c.id} style={{ cursor: 'default' }}>
                    <td><b>{c.tenantName}</b><div className="small muted mono">{c.sku ?? '—'}{c.gtin ? ` · ${c.gtin}` : ''}</div>
                      {c.catalogProductId && <Link className="small" to={`/admin/catalogo/${c.catalogProductId}`}>abrir a ficha</Link>}</td>
                    <td>{c.fieldLabel}</td>
                    <td className="small" style={{ whiteSpace: 'normal', maxWidth: 220 }}>{c.currentValue ?? '—'}</td>
                    <td className="small" style={{ whiteSpace: 'normal', maxWidth: 260 }}><b>{c.suggestedValue}</b>{c.note && <div className="muted">{c.note}</div>}
                      {c.hasAttachment && <a href={`/api/v1/admin/contests/${c.tenantId}/${c.id}/attachment`} target="_blank" rel="noreferrer">ver anexo</a>}</td>
                    <td className="small" style={c.late ? { color: 'var(--err)', fontWeight: 600 } : undefined}>{dateTime(c.dueAt).slice(0, 10)}{c.late ? ' (atrasada)' : ''}<div className="muted">aberta {dateTime(c.createdAt)}</div></td>
                    <td><Tag tone={c.status === 'open' ? 'info' : c.status === 'accepted' ? 'ok' : 'err'}>{c.status === 'open' ? 'aberta' : c.status === 'accepted' ? 'aceita' : 'recusada'}</Tag>
                      {c.response && <div className="small muted">{c.response}</div>}</td>
                    <td>{c.status === 'open' && (
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button type="button" className="btn sm" disabled={decide.isPending} onClick={() => ask(c, 'accept')}>Aceitar</button>
                        <button type="button" className="btn sm danger" disabled={decide.isPending} onClick={() => ask(c, 'reject')}>Recusar</button>
                      </div>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

/** Relatório de impacto da trava de medicamentos (antes de ligar), por conta. */
export function LockMedicineImpact() {
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ['admin', 'lock-impact'], queryFn: () => api<{ items: { tenantId: string; tenantName: string; medicineSkus: number; differingSkus: number; titleDiff: number;
    brandDiff: number; manufacturerDiff: number; anvisaDiff: number; activeListings: number; pendingCuration: number }[]; total: { differingSkus: number; activeListings: number } }>('/admin/compliance/lock-medicine-impact'), enabled: open });
  return (
    <section className="panel stack-y" style={{ gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Trava de medicamentos: impacto antes de ligar</h2>
        <span className="grow" />
        <button type="button" className="btn sm" onClick={() => setOpen(!open)}>{open ? 'Fechar' : 'Ver relatório'}</button>
      </div>
      <p className="small muted" style={{ margin: 0 }}>SKUs de medicamento vinculados ao mestre com nome, marca, fabricante ou registro diferente do mestre, e quantos anúncios ativos eles têm. Ao ligar, a trava vale para publicações novas; os anúncios ativos só mudam nos canais em lotes, com prévia e aprovação.</p>
      {open && (q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : (
        <table className="simple">
          <thead><tr><th>Conta</th><th className="num">Medicamentos</th><th className="num">Com diferença</th><th className="num">Nome</th><th className="num">Marca</th><th className="num">Fabricante</th><th className="num">Registro</th><th className="num">Anúncios ativos</th><th className="num">Aguardando curadoria</th></tr></thead>
          <tbody>
            {q.data.items.map((r) => (
              <tr key={r.tenantId}><td>{r.tenantName}</td><td className="num">{r.medicineSkus}</td><td className="num"><b>{r.differingSkus}</b></td><td className="num">{r.titleDiff}</td>
                <td className="num">{r.brandDiff}</td><td className="num">{r.manufacturerDiff}</td><td className="num">{r.anvisaDiff}</td><td className="num"><b>{r.activeListings}</b></td><td className="num">{r.pendingCuration}</td></tr>
            ))}
            <tr><td><b>Total</b></td><td /><td className="num"><b>{q.data.total.differingSkus}</b></td><td /><td /><td /><td /><td className="num"><b>{q.data.total.activeListings}</b></td><td /></tr>
          </tbody>
        </table>
      ))}
    </section>
  );
}
